import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../hooks/useApi.js';
import { fetchCampuses } from '../lib/api.js';

/**
 * Hover-driven campus selector. Mirrors `[data-school-selector]` from
 * design-source/index.html (lines 1100-1273) and the JS hover behaviour
 * from `assets/site.js initSchoolSelector()`.
 *
 * Campuses come from /api/campuses (admin-managed). A hardcoded fallback
 * is kept so the page still renders if the API is empty / unreachable.
 */
const FALLBACK_CAMPUSES = [
  {
    id: 'f1', key: 'main',
    image: 'https://images.unsplash.com/photo-1562774053-701939374585?w=1600&q=80',
    kicker: 'Main Campus · Head Office',
    rawTitle: 'House 31, <em>Road 14/A</em>',
    body: "Our administrative headquarters and senior boys' campus.",
    programmes: 'Classes VI–VIII (Boys) — January Session',
    groupLabel: 'Main Campus',
    shortLabel: 'House 31, Road 14/A',
    sortOrder: 1,
  },
];

/** Render `programmes` text (one programme per line) as <span>…<br/> rows. */
function ProgrammeList({ text }) {
  if (!text) return null;
  const lines = String(text).split(/\n+/).map((s) => s.trim()).filter(Boolean);
  return lines.map((p, i) => <span key={i}>{p}<br /></span>);
}

export function SchoolSelector() {
  const { data: api } = useApi(fetchCampuses, []);
  const campuses = (api && api.length) ? api : FALLBACK_CAMPUSES;

  // Default the active image to the first campus (lowest sortOrder).
  const firstKey = campuses[0]?.key || 'main';
  const [active, setActive] = useState(firstKey);

  // Group by groupLabel, preserving the order in which groups first appear.
  const groups = useMemo(() => {
    const order = [];
    const map = new Map();
    for (const c of campuses) {
      const g = c.groupLabel || 'Campuses';
      if (!map.has(g)) { order.push(g); map.set(g, []); }
      map.get(g).push(c);
    }
    return order.map((title) => ({ title, items: map.get(title) }));
  }, [campuses]);

  return (
    <section className="school-selector-section">
      <div className="container">
        <div className="school-selector-head reveal">
          <div className="section-eyebrow">Discover</div>
          <h2 className="section-title">Our <em>Campuses</em></h2>
          <p className="section-lede">
            {campuses.length} purpose-fitted campus{campuses.length === 1 ? '' : 'es'} across Dhanmondi — each carefully matched to a distinct educational stage, session and student community.
          </p>
        </div>

        {/* Compact mobile list — visible only under 600 px via CSS. */}
        <div className="school-mobile-list">
          {campuses.map((c) => (
            <Link to="/contact" key={c.key} className="school-mobile-card">
              <div className="img" style={{ backgroundImage: `url('${c.image || ''}')` }} aria-hidden="true" />
              <div className="copy">
                <span className="kicker">{c.kicker}</span>
                <h3 dangerouslySetInnerHTML={{ __html: c.rawTitle }} />
              </div>
              <span className="chevron" aria-hidden="true">›</span>
            </Link>
          ))}
        </div>

        <div className="school-selector" data-school-selector>
          <div className="school-selector-inner">
            <div className="school-images">
              {campuses.map((c) => (
                <div
                  key={c.key}
                  className={`school-image${active === c.key ? ' is-active' : ''}`}
                  data-school={c.key}
                >
                  <div className="img-outer">
                    <div className="img-c" style={{ backgroundImage: `url('${c.image || ''}')` }} />
                  </div>
                  <div className="content">
                    <div className="kicker">{c.kicker}</div>
                    <h2 dangerouslySetInnerHTML={{ __html: c.rawTitle }} />
                    <p>{c.body}</p>
                    <div className="address-block">
                      <strong>Programmes</strong>
                      <ProgrammeList text={c.programmes} />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="schools-list" onMouseLeave={() => setActive(firstKey)}>
              <div className="tile-wrapper">
                {groups.map((g) => (
                  <div className="tile-c" key={g.title}>
                    <div className="inner">
                      <h4>{g.title}</h4>
                      <div className="level">
                        {g.items.map((c) => (
                          <div className="level-item" key={c.key}>
                            <Link
                              to="/contact"
                              data-school={c.key}
                              className={active === c.key ? 'is-current' : ''}
                              onMouseEnter={() => setActive(c.key)}
                              onFocus={() => setActive(c.key)}
                            >
                              <span>{c.shortLabel || c.key}</span>
                            </Link>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
